import { useState } from 'react'
import { Modal, Progress } from 'antd'
import { useTranslation } from 'react-i18next'
import Papa from 'papaparse'
import {
  UploadIcon,
  DownloadIcon,
  DocumentTextIcon,
  CheckIcon,
  ChevronRightIcon,
  RefreshIcon,
  CheckCircleIcon,
  ExclamationIcon,
  XCircleIcon,
} from '@heroicons/react/outline'

import { Button } from '../../../../components'
import { generateCsvTemplate } from '../../../../utils/csvUtils'
import { computeRange, expandRangeValues, MAX_RANGE } from './zipRange'
import { updateShippingCoverageByRange } from '../../../../services/shippingMethodsServices'
import { bulkCreateShippingCoverages } from '../../../../services/shippingCoveragesBulkCreateService'
import { CoverageJobProgressPanel } from '../CoverageJobProgressPanel'

const M = 'lastmile_management.coverage_manager.bulk_upload'

// Postal-code rules per country. `length` comes from ShippingCoverage::VALUE_FORMAT_BY_COUNTRY
// — the regex behind the shipping_coverages_value_format DB CHECK, i.e. what a coverage value
// can actually be. A BR CEP is 8 digits and a MX CP 5, so validating every country against 5
// rejects every Brazilian file outright. `sample` is the template's example row and lives in
// the same entry so a new country cannot ship a length without a matching sample (or a sample
// the modal itself would then reject).
//
// Only the countries with a Last-Mile coverage screen are listed. CO is deliberately absent:
// the model regex says 8 digits while the controller's ZIPCODE_LENGTH_BY_COUNTRY says 6, and
// there is no CO coverage data to settle which is right, so it is refused outright (see
// isUnsupportedCountry) rather than measured against a guess. Same for CL, whose values are
// 3 alphanumeric characters — a digit-length rule cannot express that at all.
const ZIP_RULES_BY_COUNTRY = {
  MX: { length: 5, sample: '01000' },
  BR: { length: 8, sample: '01001000' },
}
const DEFAULT_ZIP_RULES = ZIP_RULES_BY_COUNTRY.MX
const zipRulesFor = country => ZIP_RULES_BY_COUNTRY[country] || DEFAULT_ZIP_RULES

// A method whose country has no rule above cannot be validated: applying the MX rule to
// it is what produced this bug in the first place, only with the countries swapped. Say
// the country is unsupported — the way update-by-prefix answers UNSUPPORTED_COUNTRY —
// instead of reporting every row as malformed. A method with no country at all is not
// "unsupported", it is the legacy shape that has always fallen back to 5 digits.
const isUnsupportedCountry = country => Boolean(country) && !ZIP_RULES_BY_COUNTRY[country]

// A cell has to LOOK like a postal code before we start stripping characters out of it:
// digits, optionally split by one dash (`09500-000`). sanitize_zipcode on the backend
// strips any non-digit, so without this shape check `CP 12345` and `1.000` would sail
// through as `12345` and `01000` — a label and a thousands separator silently turned
// into postal codes.
const POSTAL_CODE_SHAPE = /^\d+(-\d+)?$/

// Mirrors ShippingMethod.sanitize_zipcode, which both endpoints run before touching the
// table: drop the separator, then restore the leading zero Excel eats when it reads a
// postal code as a number. Without this a CSV saved from Excel arrives as `1001000` /
// `9500000` and a correct BR file is rejected as malformed — the very failure this
// validation is supposed to prevent, not cause.
const sanitizeZip = value => {
  const digits = String(value ?? '').replace(/\D/g, '')
  return digits.length === 4 || digits.length === 7 ? `0${digits}` : digits
}

// Whether sanitizeZip had to restore a leading zero. Padding is what lets an Excel-saved
// file through, but it is ambiguous by nature — `9500000` is Excel's `09500000` and also
// a `95000000` with a dropped digit, two different cities — so the preview says how many
// rows it touched instead of rewriting them silently.
const wasPadded = value => {
  const digits = String(value ?? '').replace(/\D/g, '')
  return digits.length === 4 || digits.length === 7
}

// A typo guard, not a capacity limit: update-by-range takes bounds and its worker walks
// them in 1000-code batches, so a real municipality range is fine, but a mistyped
// `00000000-99999999` would enqueue 100k update_all statements on the critical queue.
// Expressed as a share of the country's postal space (5%) so it means the same thing
// everywhere: for a 5-digit country that is exactly the MAX_RANGE of 5000 that guarded
// this before, and for BR it still leaves room for any real municipality range.
const maxUpdateRangeFor = zipLength => Math.round(10 ** zipLength / 20)

// The widest range a single row may carry, per mode.
const rangeLimitFor = (config, zipLength) => (config.rangeCheck.enforceMaxWidth ? MAX_RANGE : maxUpdateRangeFor(zipLength))

// bulk-create rejects a payload over MAX_BULK_COVERAGE_IDS entries with an untranslated
// TOO_MANY_COVERAGES and no per-row detail, and create expands every range into one
// coverage per code — so two valid 5000-CEP rows would pass this preview and then fail
// wholesale. The cap is on the TOTAL, so it cannot be checked row by row.
const MAX_CREATE_COVERAGES = 5_000

// The closed vocabularies a cell can spell. The key is the canonical value the rest of the
// modal reasons in; the list is every spelling accepted for it, already normalized.
//
// The Portuguese spellings are not a nicety. This modal runs in Brazil under a Portuguese
// UI, and `estado` is a false friend there: it reads as the federative unit (SP), while a
// status is a "situação". A BR file therefore arrives with `ativa` — or with a UF — and
// every row was rejected as "formato inválido", with the accepted words nowhere on screen
// because the column hint was a hardcoded Spanish string (see columnHint).
//
// One map feeds both the validator and the payload mappers on purpose: each used to match
// the canonical spelling on its own (`normalize(v) === 'activa'`), so widening only the
// validator would let `ativa` pass the preview and then serialize to `enabled: false` — a
// silent status inversion, which is worse than the rejection it replaces.
//
// The boolean spellings follow normalizeBool in BulkCostWizardModal, the sibling CSV
// wizard for this same method's costs: a spreadsheet writes a boolean column out as
// TRUE/FALSE or 1/0, and the two wizards taking different words for the same idea is
// the inconsistency an operator runs into next.
export const SI_NO = {
  si: ['si', 'sim', 'y', 'yes', 'true', '1', 'verdadero', 'verdadeiro'],
  no: ['no', 'nao', 'n', 'false', '0', 'falso'],
}
// `inativa` / `inactiva` are what the coverage list's own status filter calls a disabled
// coverage (filter_panel.status_inactive), so they are the words an operator copies into
// the file. The CSV's `desactivada` is one name for that state, not the only one on screen.
export const ACTIVA_DESACTIVADA = {
  activa: ['activa', 'ativa', 'activo', 'ativo'],
  desactivada: ['desactivada', 'desativada', 'desactivado', 'desativado', 'inactiva', 'inativa', 'inactivo', 'inativo'],
}

const WARN_THRESHOLD = 72 // offset hours above this raise a (non-blocking) warning

// Each column carries a `type` (format check) and `required` flag (empty => error).
// `name` is the CSV header identifier — language-neutral, shown verbatim.
// `hintKey` is translated at render time instead of being a literal: the hint is where the
// operator reads which words the column takes, so a Spanish literal under a Portuguese UI
// hands out spellings the file will be rejected for. A column with a closed vocabulary also
// declares `vocabKey`, the one translated list of accepted words, so the hint and the
// rejection message can never advertise different ones.
const CONFIG_BY_MODE = {
  // Columns mirror the manual create form's fields. range_start is the single CP
  // OR the start of a range; range_ends is optional (empty => single CP). Required:
  // range_start, zona and sla_horas. Uses backend field names where they matter
  // (po_box_dropoff). No "cost": a tariff is one row per weight tier
  // (`shipping_costs` is unique on method + tarif_zone + max_compound_weight) and the
  // runtime lookup only matches rows with cost > 0 AND max_compound_weight >= weight,
  // so a single cost cell cannot express a usable tariff — it would create a zone that
  // validates and then dies at label time. No "estado" either: BulkCreateWorker
  // hardcodes `enabled: true`, so the column could never take effect. A file that still
  // carries either column is warned about in the preview.
  create: {
    titleKey: 'title',
    columns: [
      { name: 'range_start', hintKey: 'hint_range_start', type: 'cp', required: true },
      { name: 'range_ends', hintKey: 'hint_range_ends', type: 'cp' },
      { name: 'zona', type: 'text', required: true },
      { name: 'zona_extendida', hintKey: 'hint_values', vocabKey: 'vocab_si_no', type: 'bool', vocab: SI_NO },
      { name: 'po_box_dropoff', hintKey: 'hint_values', vocabKey: 'vocab_si_no', type: 'bool', vocab: SI_NO },
      { name: 'sla_horas', type: 'number', required: true },
      { name: 'sla_offset', type: 'number' },
      { name: 'eta_offset', type: 'number' },
      { name: 'content_value_fee', hintKey: 'hint_percent', type: 'number' },
      { name: 'fee_offset', type: 'number' },
    ],
    // create expands every range into one coverage per code, so MAX_RANGE caps it.
    rangeCheck: { start: 'range_start', end: 'range_ends', enforceMaxWidth: true },
    warnColumn: 'sla_offset',
    templateFilename: 'plantilla_crear_coberturas.csv',
    templateSample: ['01000', '', 'Zona A', 'no', 'no', '24', '0', '0', '0', '0'],
  },
  // Update also supports ranges: range_start is the single CP or the range start;
  // range_ends is optional (empty => single CP). `estado` is OPTIONAL: the worker
  // applies it with update_all over the whole range, so a required column would
  // force an SLA-only edit to rewrite the status of every CP in the range. Blank
  // => omit `enabled` and leave each CP's status untouched, the same "keep"
  // semantics UpdateSingleCoverageWizard offers (the endpoint only requires one
  // of enabled / delivery_sla_in_hours / delivery_sla_offset per range).
  update: {
    titleKey: 'title_update',
    // `zona` reclassifies (the tariff join key into shipping_costs.tarif_zone, and for
    // Cubbo the xdock routing key); `zona_extendida` flips the ODA/OPA surcharge. They
    // are two different columns even though ops calls both "extendida", and both are
    // optional: blank means "leave this CP's value alone", the same keep semantics
    // `estado` already has. A file written from the previous template has neither column
    // and keeps working unchanged.
    columns: [
      { name: 'range_start', hintKey: 'hint_range_start', type: 'cp', required: true },
      { name: 'range_ends', hintKey: 'hint_range_ends', type: 'cp' },
      { name: 'zona', hintKey: 'hint_keep', type: 'text' },
      // The surcharge column takes the same words `estado` does, through the same map:
      // a `bool` column that declares no vocabulary rejects every cell (canonicalValue
      // reads `col.vocab`), so a BR file saying `sim` would come back malformed — the
      // exact bug the shared vocabularies were introduced to close.
      { name: 'zona_extendida', hintKey: 'hint_values_keep', vocabKey: 'vocab_si_no', type: 'bool', vocab: SI_NO },
      { name: 'sla_horas', type: 'number' },
      { name: 'sla_offset', type: 'number' },
      { name: 'estado', hintKey: 'hint_values_keep', vocabKey: 'vocab_estado', type: 'enum', vocab: ACTIVA_DESACTIVADA },
    ],
    // No single column is required any more: update-by-range needs ONE of these, not a
    // specific one. Keeping `sla_horas` mandatory would force a pure reclassification to
    // rewrite the SLA of every CP it touches — the change is a zone move, and an SLA the
    // operator typed only to satisfy the template is a silent second write.
    requireAnyOf: ['zona', 'zona_extendida', 'sla_horas', 'sla_offset', 'estado'],
    // update sends the range to update-by-range as bounds and the worker walks it in
    // 1000-code batches, so MAX_RANGE is the wrong cap here: a real BR municipality range
    // is far wider than that, and rejecting it reports a correct row as malformed. What
    // survives is the looser, country-scaled typo guard in maxUpdateRangeFor.
    rangeCheck: { start: 'range_start', end: 'range_ends', enforceMaxWidth: false },
    warnColumn: 'sla_offset',
    templateFilename: 'plantilla_actualizar_coberturas.csv',
    // estado left blank on purpose: the template's default edit changes the SLA
    // and leaves every CP's status as it is.
    templateSample: ['01000', '', '', '', '24', '0', ''],
  },
}

// Every column every mode ships, tagged with the mode it belongs to. Exported so the copy
// tests walk what actually renders: a new column reaches the operator through this list,
// and the two ways it can arrive half-built — a closed type with no vocabulary, or a hint
// key that only exists in es — are both invisible until a BR file is rejected wholesale.
export const DECLARED_COLUMNS = Object.entries(CONFIG_BY_MODE).flatMap(([mode, config]) => config.columns.map(col => [`${mode}.${col.name}`, col]))

// Every (vocabKey, vocabulary) pair the modes declare, deduped. Exported so the copy tests
// walk the columns that actually ship instead of a list kept in step by hand.
export const DECLARED_VOCABULARIES = [
  ...new Map(
    Object.values(CONFIG_BY_MODE)
      .flatMap(config => config.columns)
      .filter(col => col.vocabKey)
      .map(col => [col.vocabKey, col.vocab]),
  ),
]

const STEP_KEYS = ['upload', 'preview', 'confirm']
const PHASE_STEP = { upload: 0, preview: 1, importing: 2, done: 2 }
const PREVIEW_ROWS = 4

// The hint shown next to a column name in the upload step. Translated, with the column's
// accepted words interpolated, so what the operator is told to write is literally the list
// the validator checks against.
const columnHint = (col, t) => (col.hintKey ? t(`${M}.${col.hintKey}`, { values: col.vocabKey ? t(`${M}.${col.vocabKey}`) : undefined }) : '')

const formatBytes = bytes => {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  return `${(bytes / 1024).toFixed(1)} KB`
}

// Lowercase + strip accents so "Sí" / "SI" / "sí" all normalize to "si".
const normalize = s => String(s).trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')

// The canonical value a cell spells, or undefined when it is outside the vocabulary.
export const canonicalValue = (vocab, value) => {
  const spelling = normalize(value)
  // `vocab || {}` keeps the old `(col.values || [])` behaviour: a column that declares a
  // closed type without a vocabulary rejects every cell instead of crashing the preview.
  return Object.keys(vocab || {}).find(canonical => vocab[canonical].includes(spelling))
}

// Format check for a non-empty cell value, per the column's declared type.
const isValidFormat = (col, value, zipLength) => {
  switch (col.type) {
    case 'cp':
      return POSTAL_CODE_SHAPE.test(value) && sanitizeZip(value).length === zipLength
    case 'number':
      return /^\d+(\.\d+)?$/.test(value)
    // Both closed types read their own vocabulary: a `bool` column checked against a
    // hardcoded map while an `enum` reads `col.vocab` is how a column ends up advertising
    // one list and enforcing another.
    case 'bool':
    case 'enum':
      return canonicalValue(col.vocab, value) !== undefined
    case 'text':
    default:
      return value !== ''
  }
}

// Validate the parsed rows against the mode's rules. A row is an error if any
// required column is empty OR any filled column has the wrong format. Warnings
// (e.g. very large offsets) are imported but flagged. `breakdown` aggregates the
// problems by column + kind so the preview can say WHAT is wrong, not just how many.
const validateRows = (rows, fields, config, zipLength) => {
  const fieldMap = {}
  fields.forEach(f => {
    fieldMap[f.trim().toLowerCase()] = f
  })
  const get = (row, name) => row[fieldMap[name]]

  const errorRows = []
  const warningRows = []
  const issueCounts = {} // `${kind}:${column}` -> count
  const issueLimits = {} // same key -> the limit the rows blew past, for the copy
  const issueVocabs = {} // same key -> the column's vocabulary key, to name what IS accepted
  const keyColumn = config.columns[0].name // first column = the CP identifier

  rows.forEach((row, i) => {
    const problems = []
    config.columns.forEach(col => {
      const value = String(get(row, col.name) ?? '').trim()
      if (value === '') {
        if (col.required) problems.push({ column: col.name, kind: 'missing' })
        return
      }
      if (!isValidFormat(col, value, zipLength)) {
        // A cell outside a closed vocabulary is not malformed, it is the wrong word, and
        // "formato inválido" gives the operator nothing to correct it with — which is how
        // a whole file can be re-uploaded five times. Name the accepted words instead,
        // carrying the key so the list renders in the locale they are reading.
        problems.push(col.vocabKey ? { column: col.name, kind: 'invalid_value', vocabKey: col.vocabKey } : { column: col.name, kind: 'format' })
      }
    })

    // A row that carries only its postal code (or range) asks the endpoint to change
    // nothing: update-by-range answers NO_UPDATE_PARAMETERS and the WHOLE file is
    // refused, so catch it here and point at the one row instead. Reported without a
    // column, because no single column is at fault.
    if (config.requireAnyOf && !config.requireAnyOf.some(name => String(get(row, name) ?? '').trim() !== '')) {
      problems.push({ column: '', kind: 'no_update_fields' })
    }

    // Optional range: if an end is given it must form a valid range with the start.
    if (config.rangeCheck) {
      const start = String(get(row, config.rangeCheck.start) ?? '').trim()
      const end = String(get(row, config.rangeCheck.end) ?? '').trim()
      if (start && end) {
        const range = computeRange({ start: sanitizeZip(start), end: sanitizeZip(end) })
        // Too wide is not a format problem — saying "formato inválido" over a
        // perfectly-formed range sends the operator hunting for a typo that is not
        // there. Create expands the range itself so MAX_RANGE binds it; update only
        // sends the bounds, where the country-scaled typo guard applies. Compare the
        // width in both branches: computeRange only reports "too-large" above its own
        // MAX_RANGE, so a country whose guard is tighter than that would otherwise
        // never see it enforced.
        const limit = rangeLimitFor(config, zipLength)
        if (range.count > limit) {
          problems.push({ column: config.rangeCheck.end, kind: 'too_wide', limit })
        } else if (!range.valid && range.reason !== 'too-large') {
          // Only "too-large" is forgiven below its own limit; a mismatched length or a
          // start past the end is malformed at any width (and reports count 0).
          problems.push({ column: config.rangeCheck.end, kind: 'format' })
        }
      }
    }

    if (problems.length) {
      problems.forEach(p => {
        const key = `${p.kind}:${p.column}`
        issueCounts[key] = (issueCounts[key] || 0) + 1
        if (p.limit) issueLimits[key] = p.limit
        if (p.vocabKey) issueVocabs[key] = p.vocabKey
      })
      errorRows.push({ i, cp: get(row, keyColumn) || '—', problems })
      return
    }

    const off = parseFloat(get(row, config.warnColumn))
    if (!Number.isNaN(off) && off > WARN_THRESHOLD) {
      warningRows.push({ i, cp: get(row, keyColumn) || '—' })
    }
  })

  const breakdown = Object.entries(issueCounts).map(([key, count]) => {
    const [kind, column] = key.split(':')
    return { kind, column, count, limit: issueLimits[key], vocabKey: issueVocabs[key] }
  })

  return { errorRows, warningRows, breakdown }
}

// ---------------------------------------------------------------------------
// CSV rows → backend payloads. Headers are matched case-insensitively (the same
// lookup validateRows uses). Exported for unit testing.
// ---------------------------------------------------------------------------
const makeCellGetter = fields => {
  const map = {}
  fields.forEach(f => {
    map[String(f).trim().toLowerCase()] = f
  })
  return (row, name) => row[map[name]]
}

// Codes a row will expand into (create) or span (update). 0 when the range is unusable.
const rangeWidth = (rows, fields) => {
  const get = makeCellGetter(fields)
  return rows.map(row => {
    const start = sanitizeZip(get(row, 'range_start'))
    const end = sanitizeZip(get(row, 'range_ends')) || start
    if (!start) return 0
    const span = Number(end) - Number(start) + 1
    return span > 0 ? span : 0
  })
}

const boolFromSiNo = v => canonicalValue(SI_NO, v) === 'si'
const enabledFromEstado = v => canonicalValue(ACTIVA_DESACTIVADA, v) === 'activa'
const numOrUndef = v => {
  const s = String(v ?? '').trim()
  return s === '' ? undefined : Number(s)
}
const dropUndefined = obj => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined))

// UPDATE → PATCH /shipping-coverages/update-by-range. One CSV row = one range
// entry carrying its own status/SLA; a blank range_ends means a single CP. The
// service maps range_ends→range_end and delivery_sla→delivery_sla_in_hours.
export const rowsToUpdateRanges = (rows, fields) => {
  const get = makeCellGetter(fields)
  return rows.map(row => {
    const start = sanitizeZip(get(row, 'range_start'))
    const end = sanitizeZip(get(row, 'range_ends'))
    const statusCell = String(get(row, 'estado') ?? '').trim()
    const zoneCell = String(get(row, 'zona') ?? '').trim()
    const extendedZoneCell = String(get(row, 'zona_extendida') ?? '').trim()
    return dropUndefined({
      range_start: start,
      range_ends: end || start,
      // Blank => omit, so a row that only changes the SLA leaves the zone and the
      // surcharge of every CP in the range untouched. `zona_extendida: no` is a real
      // instruction (remove the surcharge), so it must survive as false, not be dropped.
      zone: zoneCell === '' ? undefined : zoneCell,
      extended_zone: extendedZoneCell === '' ? undefined : boolFromSiNo(extendedZoneCell),
      // Blank => omit `enabled`, so the worker's update_all leaves the status of
      // every CP in the range alone. Sending a status on an SLA-only edit would
      // re-enable (or take down) the CPs that differ from the rest of the range.
      enabled: statusCell === '' ? undefined : enabledFromEstado(statusCell),
      delivery_sla: String(get(row, 'sla_horas') ?? '').trim(),
      delivery_sla_offset: String(get(row, 'sla_offset') ?? '').trim(),
    })
  })
}

// CREATE → POST /shipping-coverages/bulk-create. bulk-create takes one coverage
// per postal code, so ranges are expanded here. Nothing here declares prerequisites:
// a zone's tariff is a ladder of weight tiers configured in the costs tab, and a zone
// missing one comes back as ZONE_HAS_NO_SHIPPING_COST, named zone by zone in the
// preview so the operator fixes it there instead of guessing.
export const rowsToCreateCoverages = (rows, fields) => {
  const get = makeCellGetter(fields)
  const coverages = []
  rows.forEach(row => {
    const start = sanitizeZip(get(row, 'range_start'))
    const end = sanitizeZip(get(row, 'range_ends'))
    const range = computeRange({ start, end: end || start })
    // An invalid range (mismatched digits, start > end, or wider than MAX_RANGE)
    // is already reported as a row error by validateRows and excluded from the
    // payload. Never fall back to a single CP: a "01000 → 99999" row would then
    // import 1 of 99,000 postal codes and look like a success.
    if (!range.valid) return
    const values = expandRangeValues(range)
    const base = dropUndefined({
      zone: String(get(row, 'zona') ?? '').trim(),
      // No `enabled`: BulkCreateWorker hardcodes `enabled: true`, so sending it
      // would only suggest the operator can choose.
      extended_zone: boolFromSiNo(get(row, 'zona_extendida')),
      po_box_dropoff: boolFromSiNo(get(row, 'po_box_dropoff')),
      delivery_sla_in_hours: numOrUndef(get(row, 'sla_horas')),
      delivery_sla_offset: numOrUndef(get(row, 'sla_offset')),
      eta_offset: numOrUndef(get(row, 'eta_offset')),
      content_value_fee: numOrUndef(get(row, 'content_value_fee')),
      fee_offset: numOrUndef(get(row, 'fee_offset')),
    })
    values.forEach(value => coverages.push({ value, ...base }))
  })
  return coverages
}

// How much of a rejection the banner shows: enough to recognize the pattern, not so
// much that a 5000-row file turns the preview into a wall of text.
export const SERVER_ERROR_SAMPLE = 6
export const SERVER_ERROR_LIST_CAP = 10

// A bulk-create 422 carries per-row codes (`rows[].errors[].code`) and cross-row
// hints (`remediation`); the banner used to print only the top-level "Validation
// failed", which names neither the CP nor the zone to fix. Group by code so the
// operator gets one line per problem, a sample of the CPs behind it, and — for a
// zone that has no tariff — the zones that DO have one, since a typo'd zone name is
// the usual cause. Returns null when the body carries no row detail, so a plain
// message (network error, job never confirmed) still renders as one line.
// Exported for unit testing.
export const summarizeServerErrors = body => {
  const rows = Array.isArray(body?.rows) ? body.rows : []
  const byCode = new Map()
  let rowCount = 0
  rows.forEach(row => {
    const errors = Array.isArray(row?.errors) ? row.errors : []
    if (errors.length) rowCount += 1
    errors.forEach(e => {
      const code = e?.code || 'UNKNOWN'
      const entry = byCode.get(code) || { code, count: 0, values: [], zones: [] }
      entry.count += 1
      const value = String(row?.value ?? '').trim()
      if (value && entry.values.length < SERVER_ERROR_SAMPLE) entry.values.push(value)
      const zone = String(e?.zone ?? row?.zone ?? '').trim()
      if (zone && !entry.zones.includes(zone)) entry.zones.push(zone)
      byCode.set(code, entry)
    })
  })
  if (!byCode.size) return null
  const availableZones = (body?.remediation?.missing_shipping_costs || []).flatMap(m => m?.available_tarif_zones || [])
  return {
    rowCount,
    codes: [...byCode.values()].sort((a, b) => b.count - a.count),
    availableZones: [...new Set(availableZones)],
  }
}

// Two side effects the CSV itself cannot express, counted for the preview:
//   update — a row carrying `estado` rewrites the status of EVERY CP in its range
//            (the worker applies it with update_all over the range, disabled rows
//            included), not only the ones that already had it.
//   create — BulkCreateWorker hardcodes `enabled: true`, so an `estado` column
//            left over from an older template cannot take effect.
//   both   — a postal code that needed a leading zero restored was read as a number
//            somewhere upstream, and the padding is a guess (see wasPadded).
export const countStatusNotices = (rows, fields, mode) => {
  const get = makeCellGetter(fields)
  const widths = rangeWidth(rows, fields)
  let statusRewrite = 0
  // A range row carrying `zona` reclassifies EVERY CP it spans, disabled ones included —
  // and the zone is the tariff join key, so a wrong one is billed wrong on both sides.
  // Same shape as statusRewrite, counted apart so the preview names the right column.
  let zoneRewrite = 0
  // Same reach, different column: `zona_extendida: no` on a range row clears the
  // surcharge from every CP it spans — including the ones that legitimately carried it,
  // which nothing records. Counted apart so the copy names the column at fault.
  let extendedZoneRewrite = 0
  let ignoredStatus = 0
  let padded = 0
  // Update no longer rejects a range over MAX_RANGE, because a real municipality range
  // is wider than that — but a mistyped range_ends looks exactly the same, and it now
  // rewrites every code it spans. Count the wide rows so the preview says so. The
  // threshold is MAX_RANGE, the width that used to be refused, not the mode's own limit:
  // anything past that limit is an error row and never reaches this count.
  let wideRows = 0
  let wideCodes = 0
  rows.forEach((row, i) => {
    if (wasPadded(get(row, 'range_start')) || wasPadded(get(row, 'range_ends'))) padded += 1
    if (mode === 'update' && widths[i] > MAX_RANGE) {
      wideRows += 1
      wideCodes += widths[i]
    }

    const hasRange = String(get(row, 'range_ends') ?? '').trim() !== ''
    if (mode === 'update' && hasRange) {
      if (String(get(row, 'zona') ?? '').trim() !== '') zoneRewrite += 1
      if (String(get(row, 'zona_extendida') ?? '').trim() !== '') extendedZoneRewrite += 1
    }

    const statusCell = String(get(row, 'estado') ?? '').trim()
    if (!statusCell) return
    if (mode === 'update') {
      if (hasRange) statusRewrite += 1
    } else if (!enabledFromEstado(statusCell)) {
      ignoredStatus += 1
    }
  })
  return { statusRewrite, zoneRewrite, extendedZoneRewrite, ignoredStatus, padded, wideRows, wideCodes }
}

/**
 * "Cargar/Actualizar coberturas por Excel" wizard.
 *   upload    — pick a file (drop zone, template, required columns)
 *   preview   — preview & client-side validation (auto-entered on a valid CSV)
 *   importing — progress while the import runs
 *   done      — confirmation summary
 *
 * `mode` ("create" | "update") swaps the title, required columns and template.
 * Preview/validation run client-side on CSV; .xlsx asks for the CSV template.
 * Only the rows that pass that validation are sent; the import itself runs on
 * the backend (update-by-range / bulk-create) and its real job is polled by
 * CoverageJobProgressPanel.
 */
export const BulkUploadCoveragesModal = ({ open, mode = 'create', shippingMethod, importedBy, onImported, onClose }) => {
  const { t, i18n } = useTranslation()
  const config = CONFIG_BY_MODE[mode] || CONFIG_BY_MODE.create

  const [phase, setPhase] = useState('upload')
  const [file, setFile] = useState(null)
  const [parsed, setParsed] = useState(null) // { fields, rows }
  const [uploadError, setUploadError] = useState(null)
  const [showWarnDetail, setShowWarnDetail] = useState(false)
  const [result, setResult] = useState(null)
  const [jobId, setJobId] = useState(null)
  const [importError, setImportError] = useState(null)

  const reset = () => {
    setPhase('upload')
    setFile(null)
    setParsed(null)
    setUploadError(null)
    setShowWarnDetail(false)
    setResult(null)
    setJobId(null)
    setImportError(null)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const finishAndView = () => {
    onImported?.()
    handleClose()
  }

  const backToUpload = () => {
    setPhase('upload')
    setFile(null)
    setParsed(null)
    setShowWarnDetail(false)
    // Clear the previous attempt's failure too, or the preview for the NEXT file
    // opens showing an error that describes the file before it.
    setImportError(null)
  }

  const handleFile = f => {
    if (!f) return
    setFile(f)
    setUploadError(null)
    setImportError(null)
    const isCsv = /\.csv$/i.test(f.name) || f.type === 'text/csv'
    if (!isCsv) {
      setUploadError(t(`${M}.csv_only`))
      return
    }
    Papa.parse(f, {
      header: true,
      skipEmptyLines: true,
      complete: results => {
        const fields = results.meta?.fields || []
        const rows = (results.data || []).filter(r => Object.values(r).some(v => String(v ?? '').trim() !== ''))
        if (!fields.length || !rows.length) {
          setUploadError(t(`${M}.parse_error`))
          return
        }
        setParsed({ fields, rows })
        setPhase('preview')
      },
      error: () => setUploadError(t(`${M}.parse_error`)),
    })
  }

  const { length: zipLength, sample: sampleZip } = zipRulesFor(shippingMethod?.country)
  const unsupportedCountry = isUnsupportedCountry(shippingMethod?.country)

  // The sample row has to be a postal code of the method's country: a 5-digit MX
  // sample handed to a BR operator is rejected by the very validation above.
  const templateSample = config.templateSample.map((cell, i) => (config.columns[i]?.type === 'cp' && cell ? sampleZip : cell))

  const downloadTemplate = () =>
    generateCsvTemplate(
      config.templateFilename,
      config.columns.map(c => c.name),
      [templateSample],
    )

  const subtitle = shippingMethod ? [shippingMethod.carrier_name, shippingMethod.delivery_type || shippingMethod.shipping_name].filter(Boolean).join(' · ') : ''

  // ----- validation (preview) -----
  const validation = parsed ? validateRows(parsed.rows, parsed.fields, config, zipLength) : null
  const total = parsed?.rows.length || 0
  const errorCount = validation?.errorRows.length || 0
  const warningCount = validation?.warningRows.length || 0

  // The rows actually sent. The preview promises the error rows are excluded
  // ("importar N filas", "N omitidas"), and the mappers coerce invalid cells into
  // valid-looking values — a blank `estado` becomes enabled:false (which DISABLES
  // the range), a non-numeric SLA becomes NaN and serializes to null. Sending an
  // error row is therefore a silent wrong write, not a rejected one.
  const errorRowIdx = new Set((validation?.errorRows || []).map(r => r.i))
  const validRows = parsed ? parsed.rows.filter((_, i) => !errorRowIdx.has(i)) : []
  // Derived from the payload itself so the count shown can never drift from it.
  const importCount = validRows.length

  // create sends one coverage per code, so what the backend counts is the expansion,
  // not the row count.
  const coveragesToCreate = mode === 'create' && parsed ? rangeWidth(validRows, parsed.fields).reduce((a, b) => a + b, 0) : 0
  const tooManyCoverages = coveragesToCreate > MAX_CREATE_COVERAGES
  const notices = parsed
    ? countStatusNotices(validRows, parsed.fields, mode)
    : { statusRewrite: 0, zoneRewrite: 0, extendedZoneRewrite: 0, ignoredStatus: 0, padded: 0, wideRows: 0, wideCodes: 0 }
  // A file built from the older template still carries `cost`. Say it is ignored:
  // silently dropping a column the operator filled in reads as "the cost was set".
  const hasIgnoredCostColumn = mode === 'create' && (parsed?.fields || []).some(f => ['cost', 'costo'].includes(normalize(f)))
  const serverErrors = importError ? summarizeServerErrors(importError) : null

  // Real import: map the validated rows to the right endpoint, then hand the
  // returned job to the shared progress panel (which polls real status).
  const handleImport = async () => {
    setImportError(null)
    setPhase('importing')
    try {
      let res
      if (mode === 'update') {
        res = await updateShippingCoverageByRange(shippingMethod.id, rowsToUpdateRanges(validRows, parsed.fields))
      } else {
        res = await bulkCreateShippingCoverages({
          shipping_method_id: shippingMethod.id,
          dry_run: false,
          coverages: rowsToCreateCoverages(validRows, parsed.fields),
        })
      }
      // Both endpoints can answer 200/422 with a validation body instead of
      // throwing, so check it for BOTH modes — an error body has no job_id and
      // would otherwise fall through and be announced as a success.
      if (res?.error_code || res?.error) {
        setImportError(res)
        setPhase('preview')
        return
      }
      if (res?.job_id) {
        setJobId(res.job_id)
      } else {
        // Both endpoints answer 202 with a job id — update-by-range has no other
        // success path (shipping_coverages_controller.rb:331-345), and a committing
        // bulk-create either returns one or the error body caught above. No id means
        // the write was never confirmed, so say that instead of inventing counts.
        setImportError({ error: t(`${M}.no_job`) })
        setPhase('preview')
      }
    } catch (e) {
      // Terminate the chain: a network error or a local throw has no e.response,
      // and an undefined message falls back to the CLIENT-validation copy, which
      // tells the operator to fix their CSV when the request never landed.
      setImportError({
        // update-by-range answers a rejection with a 422, which axios THROWS — so this
        // catch, not the `res.error_code` branch above, is where every zone guard lands.
        // Dropping the code here is what left those rejections untranslated.
        error_code: e?.response?.data?.error_code,
        error: e?.response?.data?.error || e?.response?.data?.message || e?.message || t(`${M}.error`),
      })
      setPhase('preview')
    }
  }

  // A job's STATUS is not its EFFECT. UpdateByRangeWorker counts a range that
  // matched no coverage as a success (:37-39), so "completed" routinely means
  // "0 rows changed" — precisely the report behind this ticket ("dice que todo ok
  // pero al revisar el CP no se actualiza"). Enter the summary only when the job
  // says it applied something, and name the reason whenever it did not.
  const handleJobClose = (finalStatus, jobResult) => {
    setJobId(null)
    const applied = jobResult?.total_updated_rows ?? jobResult?.created_coverages_count ?? 0
    if (finalStatus === 'completed' && applied > 0) {
      setResult({ imported: applied, skipped: errorCount, file: file?.name, date: new Date() })
      setPhase('done')
      return
    }
    const reasonKey =
      {
        unavailable: 'job_unavailable', // poll error: outcome never observed
        failed: 'job_failed',
        partial: 'job_partial',
      }[finalStatus] || 'no_rows_matched' // completed, but nothing was written
    setImportError({ error: t(`${M}.${reasonKey}`, { applied }) })
    setPhase('preview')
  }

  const fmtDate = d => {
    if (!d) return ''
    const lang = i18n.language || 'es'
    const date = d.toLocaleDateString(lang, { day: 'numeric', month: 'short', year: 'numeric' })
    const time = d.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit', hour12: false })
    return `${date}, ${time}`
  }

  const stepIndex = PHASE_STEP[phase]

  return (
    <Modal open={open} onCancel={handleClose} title={null} footer={null} width={720} className="coverage-bulk-modal" destroyOnClose>
      {/* Header */}
      <div className="flex items-center gap-3 px-6 pt-6 pb-4">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
          <UploadIcon className="h-6 w-6" />
        </div>
        <div className="min-w-0">
          <h3 className="text-xl font-semibold text-gray-900">{t(`${M}.${config.titleKey}`)}</h3>
          {subtitle && <p className="truncate text-sm text-gray-500">{subtitle}</p>}
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-3 border-y border-gray-100 bg-gray-50 px-6 py-3 text-sm">
        {STEP_KEYS.map((key, i) => {
          const done = i < stepIndex
          const current = i === stepIndex
          return (
            <div key={key} className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span
                  className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${
                    done || current ? 'bg-indigo-600 text-white' : 'border border-gray-300 text-gray-400'
                  }`}
                >
                  {done ? <CheckIcon className="h-4 w-4" /> : i + 1}
                </span>
                <span className={done || current ? 'font-semibold text-gray-900' : 'text-gray-400'}>{t(`${M}.steps.${key}`)}</span>
              </div>
              {i < STEP_KEYS.length - 1 && <ChevronRightIcon className="h-4 w-4 text-gray-300" />}
            </div>
          )
        })}
      </div>

      {/* ===== upload ===== */}
      {phase === 'upload' && (
        <>
          <div className="space-y-6 px-6 py-6">
            <label className="group flex cursor-pointer items-center gap-5 rounded-xl border-2 border-dashed border-gray-300 bg-white p-6 transition-colors hover:border-indigo-400 hover:bg-indigo-50">
              <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-gray-100">
                <DocumentTextIcon className="h-9 w-9 text-indigo-500" />
              </div>
              <div className="min-w-0">
                <div className="text-lg font-semibold text-gray-900">{t(`${M}.drop_title`)}</div>
                <div className="text-sm text-gray-500">{t(`${M}.drop_hint`)}</div>
              </div>
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={e => handleFile(e.target.files?.[0] || null)} />
            </label>

            {uploadError && <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{uploadError}</div>}

            <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl bg-gray-50 px-5 py-4">
              <div className="flex items-center gap-3">
                <DownloadIcon className="h-6 w-6 flex-shrink-0 text-indigo-500" />
                <div>
                  <div className="text-sm font-medium text-gray-900">{t(`${M}.template_question`)}</div>
                  <div className="text-sm text-gray-500">{t(`${M}.template_desc`)}</div>
                </div>
              </div>
              <button
                type="button"
                onClick={downloadTemplate}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
              >
                <DownloadIcon className="h-4 w-4" />
                {t(`${M}.download_template`)}
              </button>
            </div>

            <div>
              <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">{t(`${M}.required_columns`)}</h4>
              <div className="grid grid-cols-1 gap-y-2 gap-x-8 sm:grid-cols-2">
                {config.columns.map(c => (
                  <div key={c.name} className="flex items-center gap-2 font-mono text-sm text-gray-700">
                    <CheckIcon className={`h-4 w-4 flex-shrink-0 ${c.required ? 'text-green-500' : 'text-gray-300'}`} />
                    <span>
                      {c.name}
                      {columnHint(c, t) ? ` ${columnHint(c, t)}` : ''}
                      {!c.required && <span className="ml-1 text-gray-400">· {t(`${M}.optional_tag`)}</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4">
            <span className="text-sm text-gray-500">{t(`${M}.step_indicator`, { current: 1, total: 3 })}</span>
            <button type="button" onClick={handleClose} className="text-sm font-semibold text-gray-700 transition-colors hover:text-gray-900">
              {t('common.cancel')}
            </button>
          </div>
        </>
      )}

      {/* ===== preview ===== */}
      {phase === 'preview' && parsed && (
        <>
          <div className="space-y-5 px-6 py-6">
            <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 px-4 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <DocumentTextIcon className="h-9 w-9 flex-shrink-0 text-indigo-500" />
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-gray-900">{file?.name}</div>
                  <div className="text-xs text-gray-500">
                    {formatBytes(file?.size)} · {t(`${M}.rows_detected`, { count: total })}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={backToUpload}
                className="inline-flex flex-shrink-0 items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
              >
                <RefreshIcon className="h-4 w-4" />
                {t(`${M}.change`)}
              </button>
            </div>

            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {t(`${M}.preview_title`, { count: Math.min(PREVIEW_ROWS, total) })}
              </h4>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500">
                      {parsed.fields.map(f => (
                        <th key={f} className="whitespace-nowrap px-3 py-2.5 text-left">
                          {f}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-gray-700">
                    {parsed.rows.slice(0, PREVIEW_ROWS).map((row, ri) => (
                      <tr key={ri}>
                        {parsed.fields.map((f, ci) => (
                          <td key={f} className={`whitespace-nowrap px-3 py-2.5 ${ci === 0 ? 'font-mono text-gray-900' : ''}`}>
                            {String(row[f] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {importCount > 0 && (
              <div className="flex items-start gap-2.5 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
                <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                <span className="text-sm font-medium text-green-800">{t(`${M}.valid_title`, { count: importCount })}</span>
              </div>
            )}

            {unsupportedCountry && (
              <div className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                <XCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
                <span className="text-sm text-red-800">{t(`${M}.unsupported_country`, { country: shippingMethod?.country })}</span>
              </div>
            )}
            {tooManyCoverages && (
              <div className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                <XCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
                <span className="text-sm text-red-800">{t(`${M}.too_many_coverages`, { count: coveragesToCreate, max: MAX_CREATE_COVERAGES })}</span>
              </div>
            )}
            {notices.wideRows > 0 && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                <ExclamationIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
                <span className="text-sm text-amber-800">{t(`${M}.wide_range_notice`, { count: notices.wideRows, codes: notices.wideCodes })}</span>
              </div>
            )}
            {notices.padded > 0 && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                <ExclamationIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
                <span className="text-sm text-amber-800">{t(`${M}.padded_notice`, { count: notices.padded })}</span>
              </div>
            )}
            {notices.zoneRewrite > 0 && (
              <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <ExclamationIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
                <span className="text-sm text-amber-800">{t(`${M}.zone_rewrite_notice`, { count: notices.zoneRewrite })}</span>
              </div>
            )}
            {notices.extendedZoneRewrite > 0 && (
              <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <ExclamationIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
                <span className="text-sm text-amber-800">{t(`${M}.extended_zone_rewrite_notice`, { count: notices.extendedZoneRewrite })}</span>
              </div>
            )}
            {notices.statusRewrite > 0 && (
              <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <ExclamationIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
                <span className="text-sm text-amber-800">{t(`${M}.status_rewrite_notice`, { count: notices.statusRewrite })}</span>
              </div>
            )}

            {notices.ignoredStatus > 0 && (
              <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <ExclamationIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
                <span className="text-sm text-amber-800">{t(`${M}.create_status_ignored`, { count: notices.ignoredStatus })}</span>
              </div>
            )}

            {hasIgnoredCostColumn && (
              <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <ExclamationIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
                <span className="text-sm text-amber-800">{t(`${M}.cost_column_ignored`)}</span>
              </div>
            )}

            {warningCount > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <div className="flex items-start gap-2.5">
                  <ExclamationIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
                  <div className="text-sm text-amber-800">
                    <span className="font-medium">{t(`${M}.warnings_title`, { count: warningCount })}</span>
                    <div className="text-amber-700">
                      {t(`${M}.warnings_desc`)}{' '}
                      <button type="button" onClick={() => setShowWarnDetail(v => !v)} className="font-medium text-indigo-600 hover:text-indigo-700">
                        {t(`${M}.see_detail`)}
                      </button>
                    </div>
                    {showWarnDetail && (
                      <div className="mt-1 font-mono text-xs text-amber-700">
                        {t(`${M}.rows_affected`, {
                          list: validation.warningRows
                            .slice(0, 30)
                            .map(r => r.cp)
                            .join(', '),
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {errorCount > 0 && (
              <div className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                <XCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
                <div className="text-sm text-red-800">
                  <span className="font-medium">{t(`${M}.errors_title`, { count: errorCount })}</span>
                  {/* The breakdown below always names the real problem; this line is
                      only accurate for a missing or malformed cell, so a file whose
                      only issue is an over-wide range does not get told to hunt for
                      a typo that is not there. */}
                  {validation.breakdown.some(b => b.kind === 'missing' || b.kind === 'format') && <div className="text-red-700">{t(`${M}.errors_desc`)}</div>}
                  {validation.breakdown.length > 0 && (
                    <ul className="mt-1.5 space-y-0.5 text-xs text-red-700">
                      {validation.breakdown.map(b => (
                        <li key={`${b.kind}:${b.column}`}>
                          {/* A row-level rule (no_update_fields) has no offending column;
                              printing an empty chip and a dash reads as a parse bug. */}
                          {b.column && (
                            <>
                              <span className="font-mono font-medium">{b.column}</span>
                              {' — '}
                            </>
                          )}
                          {t(`${M}.issue_${b.kind}`, { count: b.count, max: b.limit, values: b.vocabKey ? t(`${M}.${b.vocabKey}`) : undefined })}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
            {importError && (
              <div className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                <XCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
                <div>
                  <span className="font-medium">
                    {serverErrors
                      ? t(`${M}.server_errors_title`, { count: serverErrors.rowCount })
                      : // A rejection with no per-row body still carries `error_code` —
                        // which is how every zone guard answers update-by-range. Without
                        // this the operator got the raw English sentence, on the very
                        // codes this change makes reachable from a CSV.
                        importError.error_code
                        ? t([`${M}.server_codes.${importError.error_code}`, `${M}.server_code_unknown`], { code: importError.error_code })
                        : importError.error || t(`${M}.errors_desc`)}
                  </span>
                  {!serverErrors && importError.error_code && importError.error && (
                    // The server message names the zone, the range number and the weight
                    // bands at fault; ops relays that to engineering, so keep it visible.
                    <div className="mt-1 text-xs text-red-700">{importError.error}</div>
                  )}
                  {serverErrors && (
                    <ul className="mt-1.5 space-y-1.5 text-xs text-red-700">
                      {serverErrors.codes.map(c => (
                        <li key={c.code}>
                          <span className="font-medium">{t([`${M}.server_codes.${c.code}`, `${M}.server_code_unknown`], { code: c.code })}</span>
                          {` (${c.count})`}
                          {c.zones.length > 0 && <div>{t(`${M}.server_zones_affected`, { list: c.zones.slice(0, SERVER_ERROR_LIST_CAP).join(', ') })}</div>}
                          {c.values.length > 0 && (
                            <div className="font-mono">
                              {t(`${M}.server_rows_sample`, { list: c.values.join(', ') })}
                              {c.count > c.values.length ? ` ${t(`${M}.server_more`, { count: c.count - c.values.length })}` : ''}
                            </div>
                          )}
                        </li>
                      ))}
                      {serverErrors.availableZones.length > 0 && (
                        <li>
                          {t(`${M}.server_available_zones`, {
                            list: serverErrors.availableZones.slice(0, SERVER_ERROR_LIST_CAP).join(', '),
                          })}
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4">
            <span className="text-sm text-gray-500">{t(`${M}.step2_indicator`, { count: errorCount })}</span>
            <div className="flex items-center gap-4">
              <button type="button" onClick={backToUpload} className="text-sm font-semibold text-gray-700 transition-colors hover:text-gray-900">
                {t(`${M}.back`)}
              </button>
              <button type="button" onClick={handleClose} className="text-sm font-semibold text-gray-700 transition-colors hover:text-gray-900">
                {t('common.cancel')}
              </button>
              <Button type="primary" className="px-5" disabled={importCount === 0 || tooManyCoverages || unsupportedCountry} onClick={handleImport}>
                {t(`${M}.import`, { count: importCount })}
              </Button>
            </div>
          </div>
        </>
      )}

      {/* ===== importing ===== */}
      {phase === 'importing' && (
        <>
          <div className="px-6 py-10">
            {jobId ? (
              <CoverageJobProgressPanel
                jobId={jobId}
                // A CSV that rewrites thousands of coverage rows is not something to
                // call successful on an outcome nobody observed: one failed poll
                // (a 401 in the refresh window, a 502) would otherwise render a green
                // check, refetch the list and close the modal.
                neutralOnError
                onCompleted={() => onImported?.()}
                onClose={handleJobClose}
              />
            ) : (
              <div className="mx-auto max-w-sm py-4 text-center">
                <p className="text-base font-medium text-gray-900">{t(`${M}.importing`)}</p>
                <div className="mt-4">
                  <Progress percent={100} showInfo={false} status="active" strokeColor="#4f46e5" />
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4">
            <span className="text-sm text-gray-500">{t(`${M}.step_indicator`, { current: 3, total: 3 })}</span>
          </div>
        </>
      )}

      {/* ===== done ===== */}
      {phase === 'done' && result && (
        <>
          <div className="px-6 py-10 text-center">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
              <CheckIcon className="h-10 w-10 text-green-600" />
            </div>
            <h3 className="mt-5 text-2xl font-semibold text-gray-900">{t(`${M}.done_title`)}</h3>
            <p className="mt-1 text-gray-500">{t(`${M}.done_summary`, { imported: result.imported, skipped: result.skipped })}</p>

            <dl className="mx-auto mt-6 max-w-sm space-y-2 text-left text-sm">
              <div className="flex gap-6">
                <dt className="w-28 flex-shrink-0 text-gray-500">{t(`${M}.imported_by`)}</dt>
                <dd className="text-gray-900">{importedBy || '—'}</dd>
              </div>
              <div className="flex gap-6">
                <dt className="w-28 flex-shrink-0 text-gray-500">{t(`${M}.file_label`)}</dt>
                <dd className="truncate text-gray-900">{result.file}</dd>
              </div>
              <div className="flex gap-6">
                <dt className="w-28 flex-shrink-0 text-gray-500">{t(`${M}.date_label`)}</dt>
                <dd className="text-gray-900">{fmtDate(result.date)}</dd>
              </div>
            </dl>
          </div>
          <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4">
            <span className="text-sm text-gray-500">{t(`${M}.changes_applied`)}</span>
            <div className="flex items-center gap-4">
              <button type="button" onClick={handleClose} className="text-sm font-semibold text-gray-700 transition-colors hover:text-gray-900">
                {t(`${M}.close`)}
              </button>
              <Button type="primary" className="px-5" onClick={finishAndView}>
                {t(`${M}.view_coverages`)}
              </Button>
            </div>
          </div>
        </>
      )}
    </Modal>
  )
}
